import React, { useState, useEffect } from 'react';
import { Table, Card, Tag, Typography, Space, Button, Modal, Tabs, Statistic, Row, Col, Progress, List, Empty, Tooltip, Divider, Spin } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, InfoCircleOutlined, LineChartOutlined, BarChartOutlined, PieChartOutlined, BankOutlined, TeamOutlined, DollarOutlined } from '@ant-design/icons';
import { StockHolding, CompanyAnalysis } from './types';
import { Line } from '@ant-design/plots';

const { Text, Title, Paragraph } = Typography;
const { TabPane } = Tabs;

interface Props {
  holdings: StockHolding[];
  loading?: boolean;
  isMobile?: boolean;
  isTablet?: boolean;
  performanceAttribution?: {
    topContributors: Array<{
      name: string;
      contribution: number;
      return: number;
    }>;
    bottomContributors: Array<{
      name: string;
      contribution: number;
      return: number;
    }>;
  };
}

interface ColumnType {
  title: string;
  dataIndex: string | string[];
  key: string;
  fixed?: 'left';
  width: number;
  render?: (text: any, record: StockHolding) => JSX.Element;
}

const HoldingsList: React.FC<Props> = ({ holdings, loading = false, isMobile, isTablet }) => {
  const [selectedStock, setSelectedStock] = useState<StockHolding | null>(null);
  const [localHoldings, setLocalHoldings] = useState<StockHolding[]>([]);

  useEffect(() => {
    if (holdings && holdings.length > 0) {
      setLocalHoldings(holdings);
    }
  }, [holdings]);

  const getChangeColor = (value: number) => value >= 0 ? '#3f8600' : '#cf1322';
  const getChangeIcon = (value: number) => value >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />;

  const getColumns = (): ColumnType[] => {
    const baseColumns: ColumnType[] = [
      {
        title: 'Company',
        dataIndex: 'companyName',
        key: 'companyName',
        fixed: 'left' as const,
        width: isMobile ? 120 : 200,
        render: (text: string, record: StockHolding) => (
          <div 
            onClick={() => setSelectedStock(record)}
            style={{ 
              cursor: 'pointer',
              color: '#1890ff',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            className="company-name-cell"
          >
            <span>{text}</span>
            {record.profitLoss.percentage >= 0 ? (
              <Tag color="success" style={{ marginLeft: 4 }}>
                +{record.profitLoss.percentage.toFixed(1)}%
              </Tag>
            ) : (
              <Tag color="error" style={{ marginLeft: 4 }}>
                {record.profitLoss.percentage.toFixed(1)}%
              </Tag>
            )}
          </div>
        )
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        key: 'quantity',
        width: isMobile ? 80 : 100,
        render: (value: number) => (
          <Text strong>{value.toLocaleString()}</Text>
        )
      },
      {
        title: 'Avg. Price',
        dataIndex: 'avgBuyPrice',
        key: 'avgBuyPrice',
        width: isMobile ? 90 : 120,
        render: (value: number) => (
          <Text strong>₹{value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
        )
      },
      {
        title: 'LTP',
        dataIndex: 'ltp',
        key: 'ltp',
        width: isMobile ? 80 : 100,
        render: (value: number, record: StockHolding) => (
          <Text 
            strong 
            style={{ 
              color: record.ltp > record.avgBuyPrice ? '#52c41a' : record.ltp < record.avgBuyPrice ? '#f5222d' : 'inherit'
            }}
          >
            ₹{value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
        )
      },
      {
        title: 'Current Value',
        dataIndex: 'currentValue',
        key: 'currentValue',
        width: isMobile ? 100 : 140,
        render: (value: number) => (
          <Text strong>₹{value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
        )
      },
      {
        title: 'P&L',
        dataIndex: ['profitLoss', 'absolute'],
        key: 'profitLoss',
        width: isMobile ? 90 : 120,
        render: (value: number, record: StockHolding) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {value >= 0 ? <ArrowUpOutlined style={{ color: '#52c41a' }} /> : <ArrowDownOutlined style={{ color: '#f5222d' }} />}
            <Text 
              strong 
              style={{ 
                color: value >= 0 ? '#52c41a' : '#f5222d'
              }}
            >
              ₹{Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Text>
          </div>
        )
      }
    ];

    if (!isMobile) {
      baseColumns.push(
        {
          title: 'Market Cap',
          dataIndex: 'marketCap',
          key: 'marketCap',
          width: 100,
          render: (_, record: StockHolding) => (
            <Tag color={
              record.marketCap === 'LARGE' ? 'blue' :
              record.marketCap === 'MID' ? 'cyan' :
              record.marketCap === 'SMALL' ? 'orange' :
              'purple'
            }>
              {record.marketCap}
            </Tag>
          )
        },
        {
          title: '% Change',
          dataIndex: ['profitLoss', 'percentage'],
          key: 'percentageChange',
          width: 100,
          render: (_, record: StockHolding) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {record.profitLoss.percentage >= 0 ? 
                <ArrowUpOutlined style={{ color: '#52c41a' }} /> : 
                <ArrowDownOutlined style={{ color: '#f5222d' }} />
              }
              <Text 
                strong 
                style={{ 
                  color: record.profitLoss.percentage >= 0 ? '#52c41a' : '#f5222d'
                }}
              >
                {Math.abs(record.profitLoss.percentage).toFixed(1)}%
              </Text>
            </div>
          )
        }
      );
    }

    if (!isMobile && !isTablet) {
      baseColumns.push(
        {
          title: 'Sector',
          dataIndex: ['fundamentals', 'sector'],
          key: 'sector',
          width: 120,
        },
        {
          title: 'P/E',
          dataIndex: ['fundamentals', 'pe'],
          key: 'pe',
          width: 80,
        }
      );
    }

    return baseColumns;
  };

  const CompanyAnalysisTab: React.FC<{ analysis: CompanyAnalysis }> = ({ analysis }) => {
    return (
      <div className="company-analysis">
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card title="Investment Rationale" size="small">
              <Paragraph>{analysis.investmentRationale}</Paragraph>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Valuation Metrics" size="small">
              <Row gutter={[8, 8]}>
                <Col span={12}>
                  <Statistic
                    title="Intrinsic Value"
                    value={analysis.valuationMetrics.intrinsicValue}
                    precision={2}
                    prefix="₹"
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Margin of Safety"
                    value={analysis.valuationMetrics.marginOfSafety}
                    precision={1}
                    suffix="%"
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Card title="Pros" size="small">
              <List
                size="small"
                dataSource={analysis.pros}
                renderItem={item => (
                  <List.Item>
                    <Text type="success">✓</Text> {item}
                  </List.Item>
                )}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Cons" size="small">
              <List
                size="small"
                dataSource={analysis.cons}
                renderItem={item => (
                  <List.Item>
                    <Text type="danger">✗</Text> {item}
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Card title="Analyst Recommendations" size="small">
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Buy"
                    value={analysis.analystRecommendations.buy}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Hold"
                    value={analysis.analystRecommendations.hold}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Sell"
                    value={analysis.analystRecommendations.sell}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Col>
              </Row>
              <div style={{ marginTop: 16 }}>
                <Text>Average Target Price: </Text>
                <Text strong>₹{analysis.analystRecommendations.averageTargetPrice}</Text>
              </div>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Card title="Recent News" size="small">
              <List
                size="small"
                dataSource={analysis.recentNews}
                renderItem={item => (
                  <List.Item>
                    <Tag color={
                      item.impact === 'POSITIVE' ? 'success' :
                      item.impact === 'NEGATIVE' ? 'error' : 'default'
                    }>
                      {item.impact}
                    </Tag>
                    <Text style={{ marginLeft: 8 }}>{item.headline}</Text>
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      ({item.date} - {item.source})
                    </Text>
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const SectorComparisonTab: React.FC<{ stock: StockHolding }> = ({ stock }) => {
    return (
      <div className="sector-comparison">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card title="Sector Comparison" size="small">
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Card size="small">
                    <Statistic 
                      title="P/E Ratio" 
                      value={stock.fundamentals.pe} 
                      suffix={
                        <Tooltip title="Sector P/E">
                          <Text type="secondary">(Sector: {stock.fundamentals.sectorPE})</Text>
                        </Tooltip>
                      }
                    />
                    <Progress 
                      percent={Math.min(100, (stock.fundamentals.pe / stock.fundamentals.sectorPE) * 100)} 
                      status={stock.fundamentals.pe < stock.fundamentals.sectorPE ? "success" : "exception"}
                      size="small"
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small">
                    <Statistic 
                      title="P/B Ratio" 
                      value={stock.fundamentals.pbRatio} 
                      suffix={
                        <Tooltip title="Sector P/B">
                          <Text type="secondary">(Sector: {stock.fundamentals.sectorPB || 'N/A'})</Text>
                        </Tooltip>
                      }
                    />
                    {stock.fundamentals.sectorPB && (
                      <Progress 
                        percent={Math.min(100, (stock.fundamentals.pbRatio / stock.fundamentals.sectorPB) * 100)} 
                        status={stock.fundamentals.pbRatio < stock.fundamentals.sectorPB ? "success" : "exception"}
                        size="small"
                      />
                    )}
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small">
                    <Statistic 
                      title="ROE" 
                      value={stock.fundamentals.roe || 'N/A'} 
                      suffix="%" 
                    />
                    {stock.fundamentals.sectorROE && (
                      <Progress 
                        percent={Math.min(100, ((stock.fundamentals.roe || 0) / stock.fundamentals.sectorROE) * 100)} 
                        status={(stock.fundamentals.roe || 0) > stock.fundamentals.sectorROE ? "success" : "exception"}
                        size="small"
                      />
                    )}
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Card title="Growth Comparison" size="small">
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Card size="small">
                    <Statistic 
                      title="Sales Growth" 
                      value={stock.fundamentals.salesGrowth} 
                      suffix="%" 
                    />
                    {stock.fundamentals.sectorSalesGrowth && (
                      <Progress 
                        percent={Math.min(100, (stock.fundamentals.salesGrowth / stock.fundamentals.sectorSalesGrowth) * 100)} 
                        status={stock.fundamentals.salesGrowth > stock.fundamentals.sectorSalesGrowth ? "success" : "exception"}
                        size="small"
                      />
                    )}
                  </Card>
                </Col>
                <Col span={12}>
                  <Card size="small">
                    <Statistic 
                      title="PAT Growth" 
                      value={stock.fundamentals.patGrowth} 
                      suffix="%" 
                    />
                    {stock.fundamentals.sectorPATGrowth && (
                      <Progress 
                        percent={Math.min(100, (stock.fundamentals.patGrowth / stock.fundamentals.sectorPATGrowth) * 100)} 
                        status={stock.fundamentals.patGrowth > stock.fundamentals.sectorPATGrowth ? "success" : "exception"}
                        size="small"
                      />
                    )}
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Card title="Sector Performance" size="small">
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Card size="small">
                    <Statistic 
                      title="1 Month" 
                      value={stock.sectorPerformance?.oneMonth || 'N/A'} 
                      suffix="%" 
                      valueStyle={{ color: (stock.sectorPerformance?.oneMonth || 0) >= 0 ? '#3f8600' : '#cf1322' }}
                      prefix={(stock.sectorPerformance?.oneMonth || 0) >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small">
                    <Statistic 
                      title="3 Months" 
                      value={stock.sectorPerformance?.threeMonths || 'N/A'} 
                      suffix="%" 
                      valueStyle={{ color: (stock.sectorPerformance?.threeMonths || 0) >= 0 ? '#3f8600' : '#cf1322' }}
                      prefix={(stock.sectorPerformance?.threeMonths || 0) >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small">
                    <Statistic 
                      title="1 Year" 
                      value={stock.sectorPerformance?.oneYear || 'N/A'} 
                      suffix="%" 
                      valueStyle={{ color: (stock.sectorPerformance?.oneYear || 0) >= 0 ? '#3f8600' : '#cf1322' }}
                      prefix={(stock.sectorPerformance?.oneYear || 0) >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    />
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const StockDetailsModal: React.FC<{ visible: boolean; onClose: () => void; stock: StockHolding }> = ({ visible, onClose, stock }) => {
    return (
      <Modal
        title={
          <Space>
            <Title level={4}>{stock.companyName}</Title>
            <Tag>{stock.symbol}</Tag>
            <Tag color={stock.marketCap === 'LARGE' ? 'blue' : stock.marketCap === 'MID' ? 'orange' : 'red'}>
              {stock.marketCap} Cap
            </Tag>
          </Space>
        }
        visible={visible}
        onCancel={onClose}
        width={1000}
        footer={null}
      >
        <Tabs defaultActiveKey="1">
          <TabPane tab="Overview" key="1">
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Current Value"
                    value={stock.currentValue}
                    precision={2}
                    prefix="₹"
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Investment"
                    value={stock.totalInvestment}
                    precision={2}
                    prefix="₹"
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Quantity"
                    value={stock.quantity}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Avg. Buy Price"
                    value={stock.avgBuyPrice}
                    precision={2}
                    prefix="₹"
                  />
                </Card>
              </Col>
            </Row>
            
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Card title="Profit/Loss">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Absolute"
                        value={stock.profitLoss.absolute}
                        precision={2}
                        valueStyle={{ color: getChangeColor(stock.profitLoss.absolute) }}
                        prefix={getChangeIcon(stock.profitLoss.absolute)}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Percentage"
                        value={stock.profitLoss.percentage}
                        precision={2}
                        suffix="%"
                        valueStyle={{ color: getChangeColor(stock.profitLoss.percentage) }}
                        prefix={getChangeIcon(stock.profitLoss.percentage)}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Tax Implications">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Short Term"
                        value={stock.profitLoss.shortTerm}
                        precision={2}
                        prefix="₹"
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Long Term"
                        value={stock.profitLoss.longTerm}
                        precision={2}
                        prefix="₹"
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="Fundamentals" key="2">
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Card>
                  <Statistic title="Market Cap" value={stock.fundamentals.marketCap} prefix="₹" suffix="Cr" />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic title="P/E Ratio" value={stock.fundamentals.pe} />
                  <Text type="secondary">Sector P/E: {stock.fundamentals.sectorPE}</Text>
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic title="P/B Ratio" value={stock.fundamentals.pbRatio} />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic title="EPS" value={stock.fundamentals.eps} prefix="₹" />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic title="Sales Growth" value={stock.fundamentals.salesGrowth} suffix="%" />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic title="PAT Growth" value={stock.fundamentals.patGrowth} suffix="%" />
                </Card>
              </Col>
            </Row>
            
            {stock.fundamentals.npa && (
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Card title="NPA (Bank Specific)">
                    <Row gutter={16}>
                      <Col span={12}>
                        <Statistic title="Gross NPA" value={stock.fundamentals.npa.gross} suffix="%" />
                      </Col>
                      <Col span={12}>
                        <Statistic title="Net NPA" value={stock.fundamentals.npa.net} suffix="%" />
                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Bank Metrics">
                    <Row gutter={16}>
                      {stock.fundamentals.casaRatio && (
                        <Col span={8}>
                          <Statistic title="CASA Ratio" value={stock.fundamentals.casaRatio} suffix="%" />
                        </Col>
                      )}
                      {stock.fundamentals.nim && (
                        <Col span={8}>
                          <Statistic title="NIM" value={stock.fundamentals.nim} suffix="%" />
                        </Col>
                      )}
                      {stock.fundamentals.pcr && (
                        <Col span={8}>
                          <Statistic title="PCR" value={stock.fundamentals.pcr} suffix="%" />
                        </Col>
                      )}
                    </Row>
                  </Card>
                </Col>
              </Row>
            )}
          </TabPane>

          <TabPane tab="Sector Comparison" key="3">
            <SectorComparisonTab stock={stock} />
          </TabPane>

          <TabPane tab="Company Analysis" key="4">
            {stock.companyAnalysis ? (
              <CompanyAnalysisTab analysis={stock.companyAnalysis} />
            ) : (
              <Empty description="Company analysis not available" />
            )}
          </TabPane>

          <TabPane tab="Shareholding Pattern" key="5">
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Statistic title="Promoters" value={stock.shareholding.promoters} suffix="%" />
                    </Col>
                    <Col span={6}>
                      <Statistic title="FII" value={stock.shareholding.fii} suffix="%" />
                    </Col>
                    <Col span={6}>
                      <Statistic title="DII" value={stock.shareholding.dii} suffix="%" />
                    </Col>
                    <Col span={6}>
                      <Statistic title="Public" value={stock.shareholding.public} suffix="%" />
                    </Col>
                  </Row>
                  <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    Last Updated: {stock.shareholding.lastUpdated}
                  </Text>
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="Demat Breakup" key="6">
            <Table
              dataSource={stock.dematBreakup}
              columns={[
                { 
                  title: 'Broker',
                  dataIndex: 'dematId',
                  key: 'dematId',
                  render: (text: string) => (
                    <Space>
                      {text === 'Zerodha' && <img src="/zerodha-logo.png" alt="Zerodha" style={{ width: 20, height: 20 }} />}
                      {text === 'Groww' && <img src="/groww-logo.png" alt="Groww" style={{ width: 20, height: 20 }} />}
                      {text === 'Angel One' && <img src="/angel-logo.png" alt="Angel One" style={{ width: 20, height: 20 }} />}
                      {text === 'HDFC Securities' && <img src="/hdfc-logo.png" alt="HDFC Securities" style={{ width: 20, height: 20 }} />}
                      {text === 'ICICI Direct' && <img src="/icici-logo.png" alt="ICICI Direct" style={{ width: 20, height: 20 }} />}
                      {text === 'Upstox' && <img src="/upstox-logo.png" alt="Upstox" style={{ width: 20, height: 20 }} />}
                      {text}
                    </Space>
                  )
                },
                { 
                  title: 'DP Name',
                  dataIndex: 'dpName',
                  key: 'dpName'
                },
                { 
                  title: 'DP ID',
                  dataIndex: 'dpId',
                  key: 'dpId'
                },
                { 
                  title: 'Client ID',
                  dataIndex: 'clientId',
                  key: 'clientId',
                  render: (text: string) => <Text copyable>{text}</Text>
                },
                { 
                  title: 'Quantity',
                  dataIndex: 'quantity',
                  key: 'quantity',
                  render: (quantity: number) => (
                    <Text strong>{quantity.toLocaleString()}</Text>
                  )
                },
              ]}
              pagination={false}
              size={isMobile ? 'small' : 'middle'}
              scroll={{ x: isMobile ? 600 : true }}
            />
          </TabPane>

          <TabPane tab="Dividends" key="7">
            <Table
              dataSource={stock.dividends}
              columns={[
                { title: 'Date', dataIndex: 'date', key: 'date' },
                { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (amount: number) => `₹${amount}` },
                { title: 'Type', dataIndex: 'type', key: 'type' },
              ]}
              pagination={false}
            />
          </TabPane>
        </Tabs>
      </Modal>
    );
  };

  // Add hover styles for the company name cell
  const tableStyles = `
    .company-name-cell {
      transition: all 0.3s ease;
    }
    .company-name-cell:hover {
      transform: translateX(4px);
      text-decoration: underline;
    }
    .ant-table-row {
      cursor: pointer;
      transition: all 0.3s ease;
    }
    .ant-table-row:hover {
      background-color: #f5f5f5;
    }
  `;

  return (
    <>
      <style>{tableStyles}</style>
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <Spin spinning={loading}>
          <Table
            dataSource={localHoldings}
            columns={getColumns()}
            pagination={{
              pageSize: isMobile ? 10 : 20,
              hideOnSinglePage: true,
              showSizeChanger: !isMobile,
            }}
            scroll={{ x: isMobile ? 600 : 1200 }}
            size={isMobile ? 'small' : 'middle'}
            rowKey="symbol"
            onRow={(record) => ({
              onClick: () => setSelectedStock(record),
              style: { cursor: 'pointer' }
            })}
            locale={{
              emptyText: loading ? <Spin size="small" /> : 'No holdings available'
            }}
          />
        </Spin>
      </div>
      {selectedStock && (
        <StockDetailsModal
          visible={!!selectedStock}
          onClose={() => setSelectedStock(null)}
          stock={selectedStock}
        />
      )}
    </>
  );
};

export default HoldingsList; 